import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, DataTable, StatusBadge, Sel, BranchSel, Field, Kpi, opts } from "@/components/kit";
import { api, useApi, fmtErr, dt, label, today, fileToDataUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function CompleteDialog({ c, onClose, onDone }) {
  const [f, setF] = useState({ result: "pass", comment: "", photo: null });
  const save = async () => {
    try {
      await api.post(`/checks/${c.id}/complete`, f);
      toast.success(f.result === "pass" ? "Check completed" : "Check failed — escalated");
      onDone();
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card" data-testid="check-complete-dialog">
        <DialogHeader><DialogTitle>{c.device_code} — {label(c.check_type)} check</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{c.required_check}</p>
        <div className="grid grid-cols-2 gap-2">
          {["pass", "fail"].map((r) => (
            <button key={r} data-testid={`check-result-${r}`} onClick={() => setF({ ...f, result: r })}
              className={`rounded-md border py-3 text-sm font-bold uppercase ${f.result === r ? (r === "pass" ? "border-emerald-500 bg-emerald-500/10 text-emerald-500" : "border-red-500 bg-red-500/10 text-red-500") : ""}`}>{r}</button>
          ))}
        </div>
        <Field label={`Comment${f.result === "fail" ? " (required)" : ""}`}><Textarea data-testid="check-comment" value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} /></Field>
        <Field label="Photo"><Input type="file" accept="image/*" capture="environment" data-testid="check-photo" onChange={async (e) => e.target.files[0] && setF({ ...f, photo: await fileToDataUrl(e.target.files[0]) })} /></Field>
        <Button data-testid="check-submit" onClick={save}>Submit</Button>
      </DialogContent>
    </Dialog>
  );
}

function Schedules() {
  const { can } = useAuth();
  const { data, reload } = useApi("/check-schedules", {});
  const save = async (s, patch) => {
    try {
      await api.put(`/check-schedules/${s.id}`, { ...s, ...patch });
      toast.success("Schedule updated");
      reload();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <DataTable testid="schedules-table" rows={data} columns={[
      { key: "name", label: "Schedule", render: (r) => <span className="font-semibold">{r.name}</span> },
      { key: "frequency", label: "Frequency", render: (r) => label(r.frequency) + (r.frequency === "weekly" ? ` (day ${r.weekday})` : r.frequency === "monthly" ? ` (day ${r.day_of_month})` : "") },
      { key: "times", label: "Times (Manila)", render: (r) => r.frequency === "continuous" ? "Every minute" : (
        <Input disabled={!can("configure")} defaultValue={r.times.join(", ")} className="h-8 w-44 font-mono text-xs" data-testid={`schedule-times-${r.check_type}`}
          onBlur={(e) => e.target.value !== r.times.join(", ") && save(r, { times: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
      ) },
      { key: "device_types", label: "Covers", render: (r) => <span className="text-xs text-muted-foreground">{r.device_types.map(label).join(", ")}</span> },
      { key: "active", label: "Active", render: (r) => <Switch disabled={!can("configure")} checked={r.active} data-testid={`schedule-active-${r.check_type}`} onCheckedChange={(v) => save(r, { active: v })} /> },
    ]} />
  );
}

export default function Checks() {
  const { branch, setBranch, can, user, branchName } = useAuth();
  const [f, setF] = useState({ date: today(), status: "", check_type: "", mine: false });
  const [sel, setSel] = useState(null);
  const { data, loading, reload } = useApi("/checks", { ...f, mine: f.mine || "", branch_id: branch });
  const cnt = (s) => (data || []).filter((c) => c.status === s).length;
  const verify = async (c) => {
    try {
      await api.post(`/checks/${c.id}/verify`);
      toast.success("Verified");
      reload();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  const gen = async () => {
    try {
      const { data: r } = await api.post("/checks/generate", null, { params: { date: f.date } });
      toast.success(`${r.created} check(s) generated`);
      reload();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <div className="fade-up">
      <PageHeader title="Periodic Checks" subtitle="Mandatory operational checks • overdue → assigned user & Branch Manager → IT (technical) → Super Admin" testid="checks-header">
        {can("configure", "approve") && <Button variant="outline" data-testid="checks-generate" onClick={gen}>Generate for date</Button>}
      </PageHeader>
      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list" data-testid="tab-checklist">Checklist</TabsTrigger>
          <TabsTrigger value="sched" data-testid="tab-schedules">Schedules</TabsTrigger>
        </TabsList>
        <TabsContent value="list" className="mt-4">
          <div className="mb-4 flex flex-wrap gap-2">
            <Input type="date" data-testid="checks-date" className="w-44" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
            <Sel value={f.check_type} onChange={(v) => setF({ ...f, check_type: v })} testid="checks-type-filter" allLabel="All types" options={opts(["opening", "shift", "closing", "weekly", "preventive"])} />
            <Sel value={f.status} onChange={(v) => setF({ ...f, status: v })} testid="checks-status-filter" allLabel="All statuses" options={opts(["pending", "completed", "failed", "overdue", "escalated"])} />
            <BranchSel value={branch} onChange={setBranch} testid="checks-branch" />
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.mine} onCheckedChange={(v) => setF({ ...f, mine: v })} data-testid="checks-mine" /> Assigned to me</label>
          </div>
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5">
            {["pending", "completed", "failed", "overdue", "escalated"].map((s) => <Kpi key={s} testid={`checks-count-${s}`} label={label(s)} value={cnt(s)} alert={["failed", "overdue", "escalated"].includes(s) && cnt(s) > 0} />)}
          </div>
          <DataTable testid="checks-table" loading={loading} rows={data} columns={[
            { key: "due_at", label: "Due", render: (r) => <span className="font-mono text-xs">{dt(r.due_at)}</span> },
            { key: "branch", label: "Branch", render: (r) => branchName(r.branch_id) },
            { key: "device_code", label: "Device", render: (r) => <span className="font-mono text-xs">{r.device_code}</span> },
            { key: "check_type", label: "Type", render: (r) => <StatusBadge status={r.check_type} /> },
            { key: "required_check", label: "Required check", render: (r) => <span className="text-xs">{r.required_check}</span> },
            { key: "assigned_to", label: "Assigned" },
            { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
            { key: "completed_at", label: "Done", render: (r) => dt(r.completed_at) },
            { key: "comment", label: "Comment", render: (r) => <span className="text-xs">{r.comment}</span> },
            { key: "verified_by", label: "Verified", render: (r) => r.verified_by || (["completed", "failed"].includes(r.status) && can("approve") ? <button className="text-xs text-primary underline" data-testid={`check-verify-${r.id}`} onClick={() => verify(r)}>Verify</button> : "—") },
            { key: "act", label: "", render: (r) => ["pending", "overdue", "escalated"].includes(r.status) && can("add") && <Button size="sm" data-testid={`check-do-${r.id}`} onClick={() => setSel(r)}>Record</Button> },
          ]} />
        </TabsContent>
        <TabsContent value="sched" className="mt-4"><Schedules /></TabsContent>
      </Tabs>
      {sel && <CompleteDialog c={sel} onClose={() => setSel(null)} onDone={reload} />}
      <span className="hidden">{user.id}</span>
    </div>
  );
}
